import { useState } from 'react'

import { MdOutlineCropFree } from 'react-icons/md'

import { Box, Button, Icon, Tooltip } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'

import { UTILITY_BUTTON_SIZE } from '@hivewatch/dark'

import { getTabColors } from '../shared/utils/helpers'

/**
 * Scan-sweep keyframes: a thin horizontal line travels from the top of the
 * icon to the bottom, fading in on entry and out on exit. Reinforces the
 * scanning metaphor and draws attention to this implementer-only entry point.
 *
 * The sweep is held invisible during the rotation windows so the sequence
 * reads cleanly as: rotate → sweep → rotate → sweep.
 *   - 0 – 33% (0 – 1s): parked at top, invisible while rotation snaps +
 *     contracts (rotation cycle 0 → 33%).
 *   - 33 – 40% (1 – 1.2s): fades in near the top.
 *   - 40 – 80% (1.2 – 2.4s): sweeps from top to bottom.
 *   - 80 – 88% (2.4 – 2.64s): fades out as it reaches the bottom.
 *   - 88 – 100% (2.64 – 3s): parked at bottom, invisible while the next
 *     rotation snap begins (rotation cycle 22 → 25%).
 */
const scanSweep = keyframes`
  0%, 33%   { top: 0%;   opacity: 0; }
  40%       { top: 8%;   opacity: 1; }
  80%       { top: 85%;  opacity: 1; }
  88%, 100% { top: 100%; opacity: 0; }
`

/**
 * Rotation + pulse keyframes for the icon wrapper. Runs in lockstep with
 * four scan-sweep passes (4 × 2s = 8s total). At the end of each sweep the
 * wrapper snaps 90° clockwise with a brief scale pulse, so successive sweeps
 * visually travel: ↓ (0°) → ← (90°) → ↑ (180°) → → (270°) → back to ↓.
 * The final 360° rotation brings us back to the starting orientation
 * seamlessly for the next cycle.
 */
const rotateAndPulse = keyframes`
  0%, 22%   { transform: rotate(0deg)   scale(1);    }
  25%       { transform: rotate(90deg)  scale(1.2);  }
  33%, 47%  { transform: rotate(90deg)  scale(1);    }
  50%       { transform: rotate(180deg) scale(1.2);  }
  58%, 72%  { transform: rotate(180deg) scale(1);    }
  75%       { transform: rotate(270deg) scale(1.2);  }
  83%, 97%  { transform: rotate(270deg) scale(1);    }
  100%      { transform: rotate(360deg) scale(1);    }
`

interface OpenGridTestingZoneButtonIProps {
  onClick: () => void
}

export const OpenGridTestingZoneButton = ({
  onClick,
}: OpenGridTestingZoneButtonIProps) => {
  const [isHovering, setIsHovering] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const { bgColor, hoverBgColor, iconColor, hoverIconColor } =
    getTabColors(false)

  const currentIconColor = isHovering || isFocused ? hoverIconColor : iconColor

  return (
    <Tooltip
      bg={hoverBgColor}
      color='white'
      hasArrow
      label='Enter Grid Testing Zone'
      mt='5px'
      openDelay={250}
      p='10px'
    >
      <Button
        _active={{ bgColor: hoverBgColor }}
        _focus={{ bgColor: hoverBgColor }}
        _hover={{ bgColor: hoverBgColor }}
        bgColor={bgColor}
        border='none'
        data-testid='open-grid-testing-zone-button'
        height={UTILITY_BUTTON_SIZE.height}
        onBlur={() => setIsFocused(false)}
        onClick={onClick}
        onFocus={() => setIsFocused(true)}
        onMouseOut={() => setIsHovering(false)}
        onMouseOver={() => setIsHovering(true)}
        px={5}
        type='button'
        width={UTILITY_BUTTON_SIZE.width}
      >
        <Box
          animation={`${rotateAndPulse} 12s ease-in-out infinite`}
          boxSize='18px'
          overflow='hidden'
          position='relative'
        >
          <Icon
            as={MdOutlineCropFree}
            boxSize='18px'
            color={currentIconColor}
          />
          <Box
            animation={`${scanSweep} 3s ease-in-out infinite`}
            bg={currentIconColor}
            boxShadow={`0 0 4px ${currentIconColor}`}
            h='1px'
            left={0}
            pointerEvents='none'
            position='absolute'
            right={0}
          />
        </Box>
      </Button>
    </Tooltip>
  )
}
